import { alpha, Box, Chip, Stack, Tooltip } from '@mui/material';
import IconHandler from 'components/common/icon/IconHandler';
import React, { useEffect, useRef, useState } from 'react';
import { SearchTopics } from 'api/search';
import IconMi from 'components/common/icon/IconMi';
import styles from './TopicsBlock.styles';
import useBundleTranslation from 'i18n';
import { RenderableComponent } from 'tools/types';

type IProps = {
    topics: SearchTopics;
    elementId: number | string;
    handleTagClick?: (
        isTagsOptions: boolean,
        topic: { topic_id: number | null; name: string },
        elementId: number | string,
    ) => void;
};

const TopicsBlock: RenderableComponent<IProps> = ({ topics, handleTagClick, elementId }: IProps) => {
    const [collapseTermSectionList, setCollapseTermSectionList] = useState<any>({});
    const { t } = useBundleTranslation(['components/common/element']);
    const [expanded, setExpanded] = useState<Record<string, boolean>>({});

    if (!(topics && Object.keys(topics).length > 0)) return null;

    const termsSectionKeys = Object.keys(topics);

    const toggleCollapseTermSection = (sectionKey: string) => {
        const newCollapseSectionsList = { ...collapseTermSectionList };
        newCollapseSectionsList[sectionKey] = !newCollapseSectionsList?.[sectionKey];
        setCollapseTermSectionList(newCollapseSectionsList);
    };

    const toggleExpand = (categoryKey: string) => {
        setExpanded((prev) => ({ ...prev, [categoryKey]: !prev[categoryKey] }));
    };

    const measurementRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
    const headerRef = useRef<{ [key: string]: HTMLDivElement | null }>({});
    const parentRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
    const [visibleChips, setVisibleChips] = useState<{ [key: string]: number | null }>({});

    useEffect(() => {
        const updateVisibleChips = () => {
            const newVisibleChips: { [key: string]: number | null } = {};

            Object.keys(measurementRefs.current).forEach((sectionKey) => {
                const parent = parentRefs.current[sectionKey];
                const headerWith = headerRef.current[sectionKey]?.offsetWidth || 0;
                const measurement = measurementRefs.current[sectionKey];

                if (parent && measurement) {
                    const parentWidth = parent.offsetWidth - headerWith - 80;
                    let totalWidth = 0;
                    let maxChips = 0;
                    const spacing = 1;

                    Array.from(measurement.children).forEach((chip: any) => {
                        totalWidth += chip.offsetWidth + spacing;
                        if (totalWidth <= parentWidth) {
                            maxChips++;
                        }
                    });

                    newVisibleChips[sectionKey] = maxChips;
                }
            });

            setVisibleChips(newVisibleChips);
        };

        updateVisibleChips();
        window.addEventListener('resize', updateVisibleChips);

        return () => {
            window.removeEventListener('resize', updateVisibleChips);
        };
    }, []);

    return (
        <Box sx={{ mt: 1.5 }}>
            {termsSectionKeys.length > 0 && (
                <>
                    {termsSectionKeys.map((sectionKey, i) => {
                        const section = topics[sectionKey];
                        const isExpanded = expanded[sectionKey] || false;
                        const visibleTags = isExpanded
                            ? section.topics
                            : section.topics.slice(0, visibleChips[sectionKey] as any);
                        return (
                            <Stack
                                key={i}
                                sx={styles.termSection}
                                width={1}
                                className={isExpanded ? 'collapse' : 'expand'}
                            >
                                <Stack direction={'row'} className={'term-section__header'}>
                                    <Box display="flex" ref={(el) => (headerRef.current[sectionKey] = el as any)}>
                                        <Box sx={styles.iconHolder}>
                                            {section.use_icon ? (
                                                <IconHandler
                                                    sx={{ fontSize: '14px' }}
                                                    icon={{
                                                        type:
                                                            section.use_icon == 'uploaded'
                                                                ? 'upload'
                                                                : section.use_icon,
                                                        value:
                                                            section.use_icon == 'uploaded'
                                                                ? `data:${section.icon};base64, ${section.icon}`
                                                                : section.fa_icon,
                                                    }}
                                                />
                                            ) : (
                                                <IconHandler
                                                    sx={{ fontSize: '14px' }}
                                                    icon={{ type: 'mi', value: 'tags' }}
                                                />
                                            )}
                                        </Box>

                                        <Box flexGrow={1} whiteSpace="nowrap" display="flex" mt={0.25}>
                                            {section.topic_type_name}{' '}
                                            <Box fontWeight="400" ml={0.25}>
                                                ({section.topics.length})
                                            </Box>
                                        </Box>
                                    </Box>

                                    <Box
                                        ref={(el) => (measurementRefs.current[sectionKey] = el as any)}
                                        sx={{
                                            visibility: 'hidden',
                                            position: 'absolute',
                                            height: 0,
                                            overflow: 'hidden',
                                        }}
                                    >
                                        {section.topics.map((topic, index) => (
                                            <Chip key={topic.topic_id} label={topic.name} />
                                        ))}
                                    </Box>
                                    <Box
                                        display="flex"
                                        justifyContent="space-between"
                                        width="100%"
                                        sx={{ overflow: 'hidden' }}
                                        ref={(el) => (parentRefs.current[sectionKey] = el as any)}
                                    >
                                        <Stack
                                            direction="row"
                                            flexWrap={isExpanded && visibleChips[sectionKey] ? 'wrap' : 'nowrap'}
                                            className={'term-section__items'}
                                            sx={{ overflow: 'hidden' }}
                                        >
                                            {visibleTags.map((topic: any, index: number) => {
                                                const needToCutLabel = topic.name.length > 30;
                                                const label = needToCutLabel
                                                    ? topic.name.substring(0, 30) + '...'
                                                    : topic.name;

                                                if (topic.topic_id) {
                                                    return (
                                                        <Box key={topic.topic_id}>
                                                            <Tooltip title={needToCutLabel ? topic.name : ''}>
                                                                <Chip
                                                                    onClick={() => {
                                                                        if (handleTagClick) {
                                                                            const isTagsOptions =
                                                                                sectionKey == 'default-topic-type';
                                                                            handleTagClick(
                                                                                isTagsOptions,
                                                                                topic,
                                                                                elementId,
                                                                            );
                                                                        }
                                                                    }}
                                                                    label={label}
                                                                    color="primary"
                                                                    sx={{
                                                                        lineHeight: '20px',
                                                                        fontSize: '12px',
                                                                        fontWeight: '400',
                                                                        borderRadius: '3px',
                                                                    }}
                                                                />
                                                            </Tooltip>
                                                        </Box>
                                                    );
                                                } else {
                                                    return (
                                                        <Box key={`${label}_${index}`}>
                                                            <Tooltip title={needToCutLabel ? topic.name : ''}>
                                                                <Chip
                                                                    key={label}
                                                                    label={label}
                                                                    sx={{
                                                                        lineHeight: '20px',
                                                                        fontSize: '12px',
                                                                        fontWeight: '400',
                                                                        borderRadius: '3px',
                                                                        color: (theme) =>
                                                                            alpha(theme.palette.text.primary, 0.64),
                                                                        pointerEvents: 'none',
                                                                    }}
                                                                />
                                                            </Tooltip>
                                                        </Box>
                                                    );
                                                }
                                            })}
                                        </Stack>
                                        {visibleChips[sectionKey] &&
                                            section.topics.length > visibleChips[sectionKey] && (
                                                <Box
                                                    sx={{
                                                        ...styles.iconHolder,
                                                        ...{
                                                            mr: 0,
                                                            ml: 1,
                                                            p: 0.5,
                                                            height: '20px',
                                                            color: 'primary.main',
                                                            cursor: 'pointer',
                                                            '&:hover': {
                                                                backgroundColor: (theme) =>
                                                                    alpha(theme.palette.primary.main, 0.08),
                                                                borderRadius: '3px',
                                                            },
                                                        },
                                                    }}
                                                >
                                                    <Box
                                                        onClick={() => {
                                                            toggleExpand(sectionKey);
                                                        }}
                                                    >
                                                        {t(
                                                            !expanded[sectionKey]
                                                                ? 'description_show_more'
                                                                : 'description_show_less',
                                                        )}
                                                    </Box>
                                                    <IconMi
                                                        icon={!expanded[sectionKey] ? 'chevron-down' : 'chevron-up'}
                                                        sx={{ fontSize: 16 }}
                                                    />
                                                </Box>
                                            )}
                                    </Box>
                                </Stack>
                            </Stack>
                        );
                    })}
                </>
            )}
        </Box>
    );
};

TopicsBlock.shouldRender = ({ topics }: IProps): boolean => {
    return !!topics && Object.keys(topics).length > 0;
};

export default TopicsBlock;
